import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { EnvironmentProvider } from './context/EnvironmentContext';
import { OutgoingWebhooksProvider } from './context/OutgoingWebhooksContext';
import { IncomingWebhooksProvider } from './context/IncomingWebhooksContext';
import { ClientsProvider } from './context/ClientsContext';
import { PricingRulesProvider } from './context/PricingRulesContext';
import { IntegrationsProvider } from './context/IntegrationsContext';
import { ProductsProvider } from './context/ProductsContext';
import { OrdersProvider } from './context/OrdersContext';
import { ApiKeysProvider } from './context/ApiKeysContext';
import { useClients } from './context/ClientsContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Home from './pages/Home';
import Observability from './pages/Observability';
import OutgoingWebhooks from './pages/OutgoingWebhooks';
import IncomingWebhooks from './pages/IncomingWebhooks';
import Clients from './pages/Clients';
import PricingRules from './pages/PricingRules';
import Integrations from './pages/Integrations';
import Packaging from './pages/Packaging';
import Orders from './pages/Orders';
import CalculationDetailsPage from './pages/CalculationDetailsPage';
import ApiKeys from './pages/ApiKeys';
import Demo from './pages/Demo';
import ThemeToggle from './components/ThemeToggle';
import EnvironmentSelector from './components/EnvironmentSelector';
import TenantSettingsModal from './components/TenantSettingsModal';
import CommandPalette from './components/CommandPalette';
import IncidentDrawer from './components/IncidentDrawer';
import { OperationsProvider } from './context/OperationsContext';
import QuickCreateDrawer from './components/QuickCreateDrawer';
import { ActiveCompanyPickerProvider, useActiveCompanyPicker } from './context/ActiveCompanyPickerContext';
import ScrollToTopButton from './components/ScrollToTopButton';

function AppContent() {
  const { currentTheme, theme } = useTheme();
  const { user, logout, setActiveCompany } = useAuth();
  const { clients: companies, refreshClients } = useClients();
  const { isCompanyPickerOpen, openCompanyPicker, closeCompanyPicker } = useActiveCompanyPicker();
  const { t, i18n } = useTranslation();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isTenantSettingsModalOpen, setIsTenantSettingsModalOpen] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const userMenuRef = useRef(null);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const controlButtonStyle = {
    padding: '8px 14px', borderRadius: 999, border: `1px solid ${currentTheme.border}`,
    cursor: 'pointer', background: currentTheme.surface, color: currentTheme.text,
    fontSize: '13px', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '8px'
  };
  const language = (i18n.language || 'en').toLowerCase().startsWith('ru') ? 'ru' : 'en';

  useEffect(() => {
    if (!isUserMenuOpen) return;

    const handleOutsideClick = (event) => {
      if (!userMenuRef.current) return;
      if (!userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isUserMenuOpen]);

  const displayName =
    user?.name ||
    user?.preferred_username ||
    user?.given_name ||
    user?.email ||
    'User';

  const companyName = user?.company?.name || '';
  const tenantLabel = (user?.tenant && String(user.tenant).trim()) || '';
  const roles = Array.isArray(user?.roles) ? user.roles : [];
  const isAdmin = roles.includes('administrator');
  const myCompanyId =
    user?.myCompany?.id ||
    user?.myCompany?.company_id ||
    user?.myCompany?.companyId ||
    '';
  const myCompanyName =
    user?.myCompany?.name || '';

  const activeCompanyId =
    user?.activeCompany?.id ||
    user?.activeCompany?.company_id ||
    user?.activeCompany?.companyId ||
    '';

  const activeCompanyName = user?.activeCompany?.name || '';

  const shouldShowActiveCompanyName =
    isAdmin &&
    Boolean(activeCompanyId) &&
    String(activeCompanyId) !== String(myCompanyId);

  useEffect(() => {
    if (!isCompanyPickerOpen) return;
    refreshClients?.();
    setSelectedCompanyId(activeCompanyId || myCompanyId || '');
  }, [isCompanyPickerOpen, activeCompanyId, myCompanyId, refreshClients]);

  return (
    <div style={{
      backgroundColor: currentTheme.background,
      color: currentTheme.text,
      transition: 'all 0.3s ease'
    }}>
       <div className="topbar">
         {user && <CommandPalette />}
         {user && <button type="button" className="quick-create-trigger" onClick={() => setQuickCreateOpen(true)}>+ {t('create')}</button>}
         {user && isAdmin && (
           <a
             className="topbar-icon-button"
             href="https://documenter.getpostman.com/view/33984706/2sBYAswWrR"
             target="_blank"
             rel="noopener noreferrer"
             title={t('documentation')}
             aria-label={t('documentation')}
           >
             <svg viewBox="0 0 24 24" aria-hidden="true">
               <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
               <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
               <path d="M8 7h8M8 11h6" />
             </svg>
           </a>
         )}
         {user && <EnvironmentSelector />}
         {user && <ThemeToggle />}
         <div className="language-switch" aria-label="Language">
           <button type="button" className={language === 'en' ? 'is-active' : ''} onClick={() => changeLanguage('en')} aria-pressed={language === 'en'}>EN</button>
           <button type="button" className={language === 'ru' ? 'is-active' : ''} onClick={() => changeLanguage('ru')} aria-pressed={language === 'ru'}>RU</button>
         </div>

              {user && (
                <div ref={userMenuRef} style={{ position: 'relative' }}>
                  <button
                    onClick={() => setIsUserMenuOpen((v) => !v)}
                    className="topbar-icon-button"
                    aria-haspopup="menu"
                    aria-expanded={isUserMenuOpen}
                    title={displayName}
                  >
                    <span className="oi oi-person" aria-hidden="true" />
                  </button>

                  {isUserMenuOpen && (
                    <div
                      role="menu"
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 8px)',
                        right: 0,
                        minWidth: 260,
                        padding: 12,
                        borderRadius: 12,
                        background: currentTheme.surface,
                        border: `1px solid ${currentTheme.border}`,
                        boxShadow: currentTheme.cardShadow,
                        color: currentTheme.text,
                        zIndex: 1000
                      }}
                    >
                      <div style={{ fontWeight: 600, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {displayName}
                      </div>
                      {(() => {
                        const primaryCompanyName = (myCompanyName && String(myCompanyName).trim()) || (companyName && String(companyName).trim()) || '';
                        if (!primaryCompanyName) {
                          return <div style={{ marginBottom: 12 }} />;
                        }
                        const line = tenantLabel ? `${primaryCompanyName} (${tenantLabel})` : primaryCompanyName;
                        return (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              marginBottom: 12,
                              minWidth: 0,
                            }}
                          >
                            <div
                              style={{
                                flex: 1,
                                color: currentTheme.textSecondary,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              title={line}
                            >
                              {line}
                            </div>
                            {isAdmin && tenantLabel && (
                              <button
                                type="button"
                                onClick={() => {
                                  setIsUserMenuOpen(false);
                                  setIsTenantSettingsModalOpen(true);
                                }}
                                aria-label={t('tenantSettings')}
                                title={t('tenantSettings')}
                                style={{
                                  flexShrink: 0,
                                  height: 28,
                                  width: 28,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: 8,
                                  border: `1px solid ${currentTheme.border}`,
                              background: currentTheme.background,
                                  color: currentTheme.textSecondary,
                                  cursor: 'pointer',
                                  padding: 0,
                                }}
                              >
                                <span className="oi oi-cog" aria-hidden="true" />
                              </button>
                            )}
                          </div>
                        );
                      })()}

                      {myCompanyId && (
                        <div
                          style={{
                            color: currentTheme.textSecondary,
                            marginTop: companyName ? -6 : 0,
                            marginBottom: 12,
                            fontSize: 12,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={shouldShowActiveCompanyName ? `${activeCompanyName} (${activeCompanyId})` : companyName}
                        >
                          {shouldShowActiveCompanyName ? `${t('activeCompany')}: ${activeCompanyName}` : ''}
                        </div>
                      )}

                      {roles.length > 0 && (
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 6,
                            marginBottom: 12
                          }}
                        >
                          {roles.map((role) => (
                            <span
                              key={role}
                              style={{
                                padding: '4px 8px',
                                borderRadius: 999,
                                fontSize: 12,
                                lineHeight: 1.2,
                                background: currentTheme.background,
                                color: currentTheme.textSecondary,
                                border: `1px solid ${currentTheme.border}`
                              }}
                              title={role}
                            >
                              {role}
                            </span>
                          ))}
                        </div>
                      )}

                      {isAdmin && (
                        <button
                          onClick={() => {
                            setIsUserMenuOpen(false);
                            openCompanyPicker();
                          }}
                          style={{
                            ...controlButtonStyle,
                            width: '100%',
                            justifyContent: 'center',
                            marginBottom: 10,
                          }}
                        >
                          {t('changeCompany')}
                        </button>
                      )}

                      <button
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          logout();
                        }}
                        style={{ ...controlButtonStyle, marginLeft: 'auto' }}
                      >
                        {t('logout')}
                      </button>
                    </div>
                  )}
                </div>
              )}

      </div>

      {isCompanyPickerOpen && (
        <div
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeCompanyPicker();
          }}
          style={{
            position: 'fixed',
            inset: 0,
            background: theme === 'light' ? 'rgba(17, 24, 39, 0.45)' : 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: 2000,
          }}
        >
          <div
            style={{
              width: 'min(560px, 100%)',
              borderRadius: 16,
              background: currentTheme.surface,
              border: `1px solid ${currentTheme.border}`,
              boxShadow: currentTheme.cardShadow,
              color: currentTheme.text,
              padding: 16,
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>
              {t('chooseActiveCompany')}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: currentTheme.textSecondary }}>
                {t('company')}
              </div>
              <select
                value={selectedCompanyId}
                onChange={(e) => setSelectedCompanyId(e.target.value)}
                style={{
                  width: '100%',
                  borderRadius: 12,
                  padding: '10px 12px',
                  border: `1px solid ${currentTheme.border}`,
                  background: currentTheme.background,
                  color: currentTheme.text,
                  outline: 'none',
                }}
              >
                <option value="">{t('select')}</option>
                {(Array.isArray(companies) ? companies : []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name || c.id}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={closeCompanyPicker}
                style={{
                  ...controlButtonStyle,
                  background: 'transparent',
                  color: currentTheme.text,
                  border: `1px solid ${currentTheme.border}`,
                }}
              >
                {t('Cancel')}
              </button>
              <button
                onClick={() => {
                  const list = Array.isArray(companies) ? companies : [];
                  const next = list.find((c) => String(c.id) === String(selectedCompanyId));
                  if (next) {
                    setActiveCompany(next);
                  }
                  closeCompanyPicker();
                }}
                disabled={!selectedCompanyId}
                style={{
                  ...controlButtonStyle,
                  opacity: selectedCompanyId ? 1 : 0.6,
                  cursor: selectedCompanyId ? 'pointer' : 'not-allowed',
                }}
              >
                {t('apply')}
              </button>
            </div>
          </div>
        </div>
      )}

      <TenantSettingsModal
        open={isTenantSettingsModalOpen}
        onClose={() => setIsTenantSettingsModalOpen(false)}
        theme={theme}
        currentTheme={currentTheme}
        tenantLabel={tenantLabel}
        controlButtonStyle={controlButtonStyle}
      />
      {user && <IncidentDrawer />}
      <QuickCreateDrawer open={quickCreateOpen} onClose={() => setQuickCreateOpen(false)} />

      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/observability" element={
          <ProtectedRoute>
            {isAdmin ? <Observability /> : <Navigate to="/" replace />}
          </ProtectedRoute>
        } />
        <Route path="/clients" element={
          <ProtectedRoute>
            {isAdmin ? <Clients /> : <Navigate to="/" replace />}
          </ProtectedRoute>
        } />
        <Route path="/orders" element={<Navigate to="/calculations" replace />} />
        <Route path="/calculations" element={
          <ProtectedRoute>
            <ProductsProvider>
              <Orders />
            </ProductsProvider>
          </ProtectedRoute>
        } />
        <Route path="/calculations/:orderNumber" element={
          <ProtectedRoute>
            <ProductsProvider><CalculationDetailsPage /></ProductsProvider>
          </ProtectedRoute>
        } />
        <Route path="/outgoing_webhooks" element={
          <ProtectedRoute>
            <OutgoingWebhooks />
          </ProtectedRoute>
        } />
        <Route path="/integrations" element={
          <ProtectedRoute>
            <Integrations />
          </ProtectedRoute>
        } />
        <Route path="/packaging" element={
          <ProtectedRoute>
            <Packaging />
          </ProtectedRoute>
        } />
        <Route path="/pricing_rules" element={
          <ProtectedRoute>
            {isAdmin ? <PricingRules /> : <Navigate to="/" replace />}
          </ProtectedRoute>
        } />
        <Route path="/api_keys" element={
          <ProtectedRoute>
            {isAdmin ? <ApiKeys /> : <Navigate to="/" replace />}
          </ProtectedRoute>
        } />
        <Route path="/incoming_webhooks" element={
          <ProtectedRoute>
            <IncomingWebhooks />
          </ProtectedRoute>
        } />
        <Route path="/demo" element={
          <ProtectedRoute>
            {isAdmin ? <Demo /> : <Navigate to="/" replace />}
          </ProtectedRoute>
        } />
        <Route path="/" element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        } />
      </Routes>
      {user && <ScrollToTopButton />}
    </div>
  );
}

function App() {
    return (
        <Router>
          <Routes>
            <Route
              path="*"
              element={
                <AuthProvider>
                  <OutgoingWebhooksProvider>
                    <IncomingWebhooksProvider>
                      <ClientsProvider>
                        <ActiveCompanyPickerProvider>
                        <PricingRulesProvider>
                          <IntegrationsProvider>
                            <ProductsProvider>
                              <OrdersProvider>
                                <ApiKeysProvider>
                                <ThemeProvider>
                                  <EnvironmentProvider>
                                    <OperationsProvider>
                                      <AppContent/>
                                    </OperationsProvider>
                                  </EnvironmentProvider>
                                </ThemeProvider>
                                </ApiKeysProvider>
                              </OrdersProvider>
                            </ProductsProvider>
                          </IntegrationsProvider>
                        </PricingRulesProvider>
                        </ActiveCompanyPickerProvider>
                      </ClientsProvider>
                    </IncomingWebhooksProvider>
                  </OutgoingWebhooksProvider>
                </AuthProvider>
              }
            />
          </Routes>
        </Router>
    );
}

export default App;
